import { Modal, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { Key, ReactNode, useCallback, useEffect, useState } from 'react';
import { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import IWarehouseLoanTransaction from '~Api/Warehouse/IWarehouseLoanTransaction';
import AbaTypeEnum from '~Api/WarehouseLoanTransactionAba/AbaTypeEnum';
import { IGlobalState } from '~reducer';
import SelectModalFooter from '~UI/SelectModalFooter';
import { currencyFormatter } from '~utilities/formatters';
import ICheckboxProps from '~utilities/ICheckboxProps';
import { IDictionary } from '~utilities/IDictionary';
import { abasAddAction, unallocatedTransactionsListAction } from '~WarehouseLoanTransactionAbas/actions';
import { unallocatedTransactionsSelector } from '~WarehouseLoanTransactionAbas/selectors';

const titles: IDictionary<string> = {
    [AbaTypeEnum.FinRepaymentPrincipal]: 'Add FIN Transfer',
    [AbaTypeEnum.FitRepaymentPrincipal]: 'Add FIT Transfer',
    [AbaTypeEnum.FnomRepaymentPrincipal]: 'Add FNOM Transfer',
    [AbaTypeEnum.Fwt1RepaymentPrincipal]: 'Add FWT1 Transfer',
    [AbaTypeEnum.Fwt2RepaymentPrincipal]: 'Add FWT2 Transfer',
};

export default function AddModal(props: {
    abaType: AbaTypeEnum;
    isOpen: boolean;
    onClose: () => void;
}): ReactElement {
    const { abaType, isOpen, onClose } = props;

    const [ selectedRowKeys, setSelectedRowKeys ] = useState<Key[]>(null);

    const unallocatedTransactions: IDictionary<IWarehouseLoanTransaction> = useSelector((state: IGlobalState): IDictionary<IWarehouseLoanTransaction> => unallocatedTransactionsSelector(state, abaType));

    const actuallySelectedRowKeys: string[] = selectedRowKeys as string[] || _.map(unallocatedTransactions, (unallocatedTransaction: IWarehouseLoanTransaction): string => unallocatedTransaction.uuid);

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!unallocatedTransactions) {
            dispatch(unallocatedTransactionsListAction(abaType));
        }
    }, [
        abaType,
        dispatch,
        unallocatedTransactions,
    ]);

    const columns: ColumnsType<IWarehouseLoanTransaction> = [
        {
            dataIndex: 'transactionTime',
            defaultSortOrder: 'descend',
            render: (warehouseLoanTransactionTime: string): ReactNode => dayjs(warehouseLoanTransactionTime).format('DD/MM/YYYY'),
            sorter: (a: IWarehouseLoanTransaction, b: IWarehouseLoanTransaction): number => dayjs(a.transactionTime) > dayjs(b.transactionTime) ? 1 : -1,
            title: 'Date',
            width: 200,
        },
        {
            dataIndex: 'loanCode',
            render: (loanCode: string, row: IWarehouseLoanTransaction): ReactNode => <Link to={`/loans/${row.loanUuid}`}>{loanCode}</Link>,
            title: 'Loan',
        },
        {
            dataIndex: 'amountPrincipal',
            render: (amountPrincipal: number): ReactNode => currencyFormatter.format(amountPrincipal),
            title: 'Principal',
            width: 200,
        },
    ];

    const onChangeSelectedRows: (rowKeys: Key[]) => void = useCallback((rowKeys: Key[]): void => {
        setSelectedRowKeys(rowKeys);
    }, []);

    const getCheckboxProps: (row: IWarehouseLoanTransaction) => ICheckboxProps = useCallback((row: IWarehouseLoanTransaction): ICheckboxProps => ({
        className: null,
        disabled: false,
        key: row.loanTransactionUuid,
    }), []);

    const rowSelection: TableRowSelection<IWarehouseLoanTransaction> = {
        getCheckboxProps,
        onChange: onChangeSelectedRows,
        selectedRowKeys: actuallySelectedRowKeys,
    };

    const onOk: () => void = useCallback((): void => {
        dispatch(abasAddAction(abaType, actuallySelectedRowKeys));
        onClose();
    }, [
        abaType,
        actuallySelectedRowKeys,
        dispatch,
        onClose,
    ]);

    const total: number = unallocatedTransactions ? _.reduce(
        actuallySelectedRowKeys,
        (sum: number, selectedRowKey: string): number => sum + unallocatedTransactions[selectedRowKey].amountPrincipal,
        0,
    ) : 0;

    const footer: ReactNode = (
        <SelectModalFooter
            count={actuallySelectedRowKeys.length}
            onCancel={onClose}
            onOk={onOk}
            total={total}
        />
    );

    return (
        <Modal
            footer={footer}
            onCancel={onClose}
            open={isOpen}
            title={titles[abaType]}
            width={1000}
        >
            <Table
                columns={columns}
                dataSource={_.values(unallocatedTransactions)}
                loading={!unallocatedTransactions}
                pagination={false}
                rowKey='uuid'
                rowSelection={rowSelection}
                scroll={{ y: 500 }}
                size='middle'
            />
        </Modal>
    );
}
